import { isResourceOfType, OpalApiResponseDocument } from '@opallabs/api/v2'
import { type User } from '@opallabs/concepts'
import type {
  ApprovalResponse,
  Assignment,
  StageTypes,
  TaskResponse,
  Workflow,
  WorkflowStatus,
} from '../../../types'
import {
  buildAssignments,
  buildResponses,
  buildStatus,
  buildWorkflow,
} from '../selectors'

type WorkflowsDocument = OpalApiResponseDocument<'ReadWorkflowsV2', 200>

export const selectWorkflow = (document: WorkflowsDocument): Workflow | null =>
  buildWorkflow({
    workflow: document.data[0],
    stages: (document.included ?? [])
      .filter(isResourceOfType('stage', 'V2'))
      // v2 soft-deletes stages in place. v3 has no `deleted_at`.
      .filter((stage) => !stage.attributes.deleted_at),
  })

export const selectStatus = (
  document: WorkflowsDocument
): WorkflowStatus | null => {
  const included = document.included ?? []

  return buildStatus({
    workflow: document.data[0],
    assignments: included.filter(isResourceOfType('assignment', 'V2')),
    responses: included.filter(isResourceOfType('response', 'V2')),
  })
}

export const selectAssignments = (
  document: OpalApiResponseDocument<'ReadStageV2', 200>
): Array<Assignment> =>
  buildAssignments(
    (document.included ?? []).filter(isResourceOfType('assignment', 'V2'))
  )

export const selectResponses = (
  document: OpalApiResponseDocument<'ReadAssignmentV2', 200>,
  stageType: StageTypes
): Array<TaskResponse | ApprovalResponse> =>
  buildResponses(
    (document.included ?? []).filter(isResourceOfType('response', 'V2')),
    stageType
  )

export const selectUser = (
  document: OpalApiResponseDocument<'ReadUserV2', 200>
): User => {
  const assetReference = document.included?.filter(
    isResourceOfType('asset_reference', 'V2')
  )[0]

  return {
    uuid: document.data.attributes.uuid,
    name: document.data.attributes.full_name,
    asset: assetReference
      ? {
          id: assetReference.attributes.uuid,
          url: assetReference.attributes.full_url ?? '',
          title: 'User Avatar',
        }
      : undefined,
    legacyId: document.data.id,
    type: 'user',
  }
}
